import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { HelpCircle, PlayCircle, ExternalLink } from 'lucide-react';
import { TUTORIAL_VIDEOS } from '../pages/AppTutorials';

/**
 * A small "?" button that opens a mini modal playing the matching tutorial video.
 * Use inline next to a page heading:
 *   <TutorialHint videoId="HEA-CVoURs4" />
 * The videoId must match one of the videos in TUTORIAL_VIDEOS.
 */
const TutorialHint = ({ videoId, className = '' }) => {
  const [open, setOpen] = useState(false);
  const video = TUTORIAL_VIDEOS.find((v) => v.id === videoId);
  if (!video) return null;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        title={`Watch: ${video.title}`}
        aria-label={`Watch tutorial: ${video.title}`}
        className={`inline-flex items-center justify-center w-8 h-8 rounded-full bg-teal-100 text-teal-700 hover:bg-teal-200 hover:text-teal-900 transition-colors ${className}`}
        data-testid={`tutorial-hint-btn-${videoId}`}
      >
        <HelpCircle className="w-5 h-5" />
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          className="max-w-2xl p-0 overflow-hidden"
          data-testid={`tutorial-hint-modal-${videoId}`}
        >
          <DialogHeader className="px-6 pt-6 pb-3 border-b">
            <DialogTitle className="flex items-center gap-2 text-[#2B6B7F]">
              <PlayCircle className="w-5 h-5 text-teal-600" />
              How to use: {video.title}
              <span className="ml-2 text-xs text-slate-400 font-normal">{video.duration}</span>
            </DialogTitle>
            <DialogDescription>{video.description}</DialogDescription>
          </DialogHeader>
          <div className="p-4">
            <div className="aspect-video w-full bg-black rounded-lg overflow-hidden">
              <iframe
                src={`https://www.youtube.com/embed/${video.id}`}
                title={video.title}
                className="w-full h-full border-0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
            <div className="flex items-center justify-end pt-3">
              <a
                href={`/tutorials#${video.id}`}
                className="text-sm text-teal-700 hover:text-teal-900 flex items-center gap-1"
                data-testid={`tutorial-hint-open-full-${videoId}`}
              >
                See all tutorials <ExternalLink className="w-3 h-3" />
              </a>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default TutorialHint;
